import { Capacitor } from "@capacitor/core";
import { motion } from "framer-motion";
import { useCallback, useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import {
  getReferralRewardDescription,
  getReferralRewardTitle,
} from "@/modules/referralRewards";
import {
  useLegacyReferAFriend,
  useShareReferralReward,
} from "@/modules/referralRewards";
import { LocalStorageService } from "@/modules/storage";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ShareGiftBox from "atoms/icon/icons/share-gift-box.svg";
import ShareV3 from "atoms/icon/icons/share-v3.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { useClipboard } from "hooks";
import {
  formatPlatformNameForReferral,
  formatTitleForReferral,
} from "organisms/refer-a-friend/utils";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

export const V3ShareFriendsPage_URL = "/signin/share-friends";

type PlatformLoaderData = {
  platformSettings?: {
    rewardsEnabled?: boolean | null;
    patientPersonalisationEnabled?: boolean | null;
  } | null;
} | null;

const resolveNextPath = (platform: PlatformLoaderData) =>
  Capacitor.isNativePlatform()
    ? "/signin/notifications"
    : platform?.platformSettings?.patientPersonalisationEnabled
    ? "/personalise"
    : "/home";

const load = async () => {
  const platform = await queryClient
    .fetchQuery({
      queryKey: ["/platform/current"],
      queryFn: () => get("/platform/current"),
    })
    .catch(() => null);

  const user = await queryClient
    .fetchQuery({
      queryKey: ["/user"],
      queryFn: () => get("/user"),
    })
    .catch(() => null);

  const referralRewardsEnabled = platform?.platformSettings?.rewardsEnabled;

  const platformLocationId = user?.platformLocation?.id;
  if (!platformLocationId) {
    throw redirect(resolveNextPath(platform));
  }

  const activeSignupReward = await queryClient
    .fetchQuery({
      queryKey: [
        "/incentives/{platformLocationId}/active-signup-reward",
        platformLocationId,
      ],
      queryFn: () =>
        get("/incentives/{platformLocationId}/active-signup-reward", {
          platformLocationId,
        }),
    })
    .catch(() => null);

  // If no signup reward exists, redirect
  if (!activeSignupReward) {
    throw redirect(resolveNextPath(platform));
  }

  let availableReferralReward = null;
  if (referralRewardsEnabled && activeSignupReward) {
    availableReferralReward = await queryClient
      .fetchQuery({
        queryKey: [
          "/incentives/{platformLocationId}/available-referral-reward",
          platformLocationId,
        ],
        queryFn: () =>
          get("/incentives/{platformLocationId}/available-referral-reward", {
            platformLocationId,
          }),
      })
      .catch(() => null);
  }

  return waitObj({
    platform,
    user,
    activeSignupReward,
    availableReferralReward,
    referralRewardsEnabled,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const v3ShareFriendsLoader: LoaderFunction = () => load();

export default function V3ShareFriendsPage() {
  const navigate = useNavigate();
  const loaderData = useLoaderData() as LoaderData;
  const {
    platform,
    activeSignupReward,
    availableReferralReward,
    referralRewardsEnabled,
    user,
  } = loaderData;

  // Determine previous page based on gift card flow
  const isGiftCardClaim =
    LocalStorageService.getItem("isGiftCardClaim") === "true";
  const prevPage = isGiftCardClaim
    ? "/signin/gift-card-redeem"
    : "/signin/rewards";

  const handleNext = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      navigate("/signin/notifications");
    } else {
      navigate(
        platform?.platformSettings?.patientPersonalisationEnabled
          ? "/personalise"
          : "/home"
      );
    }
  }, [navigate, platform?.platformSettings?.patientPersonalisationEnabled]);

  // Setup for both referral flows
  const [referralUrl, setReferralUrl] = useState<string>("");
  const { copyToClipboard, isLoading: isCopyingLink } = useClipboard();

  const referrableRewardTitle = formatTitleForReferral(
    activeSignupReward?.title
  );
  const formattedPlatformName = formatPlatformNameForReferral(platform?.name);

  // Legacy referral hook (always called - React rules of hooks)
  const { shareLegacyReferral, handleCopyLink: handleLegacyCopyLink } =
    useLegacyReferAFriend({
      rewardsEnabled: !!referralRewardsEnabled,
      platform: platform!,
      title: referrableRewardTitle || "",
      formattedPlatformName: formattedPlatformName || "",
      copyToClipboard: (text: string) => {
        copyToClipboard(text);
      },
      isCopyingLink,
      referralUrl,
      setReferralUrl,
    });

  // New referral rewards hook (always called - React rules of hooks)
  const { shareReferralReward } = useShareReferralReward({
    platform: platform!,
    user: user!,
    rewardTitle: activeSignupReward?.title || "",
  });
  // If availableReferralReward exists, use the new referral rewards flow
  // Otherwise, use the legacy referral flow
  const handleReferFriendClick = async () => {
    if (availableReferralReward) {
      const generatedUrl = await shareReferralReward();

      if (generatedUrl) {
        setReferralUrl(generatedUrl);
      }
      return;
    }

    shareLegacyReferral();
  };

  // Handle copy link
  const handleCopyLink = async () => {
    if (referralUrl) {
      await copyToClipboard(referralUrl);
      return;
    }

    if (!availableReferralReward) {
      handleLegacyCopyLink();
    }
  };

  const shareButtonLabel = availableReferralReward
    ? "Refer a friend"
    : "Invite friends";

  const renderCopyLinkButton = () => {
    if (!referralUrl) {
      return null;
    }

    return (
      <div className="mt-1 w-full">
        <Button
          style="secondary"
          fullWidth
          onClick={handleCopyLink}
          disabled={isCopyingLink}
        >
          Copy link
        </Button>
      </div>
    );
  };

  return (
    <MobileViewportContainer header={<OnboardingBackButton prev={prevPage} />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center gap-9"
      >
        {/* Share Icon - 36px gap after */}
        <Icon svg={ShareV3} size="large" color="brandColor" />
        {/* Title - 36px gap after */}
        <p className="w-full shrink-0 text-center font-serif text-[30px] font-normal italic leading-[32px] text-[#091f24]">
          {referralRewardsEnabled && availableReferralReward
            ? "Share the love."
            : "Help friends and family feel their best."}
        </p>
        {/* Card with message */}
        <div className="relative w-full shrink-0 overflow-hidden rounded-[15px] bg-white shadow-[0px_2px_10px_0px_rgba(0,0,0,0.06)]">
          {/* Background gift box decoration - only show when referral rewards available */}
          {referralRewardsEnabled && availableReferralReward && (
            <div className="absolute -bottom-5 -right-12 h-[195px] w-[192px]">
              <Icon
                svg={ShareGiftBox}
                size="x3large"
                color="brandColor"
                className="h-full w-full"
              />
            </div>
          )}

          {/* Card content */}
          {referralRewardsEnabled && availableReferralReward ? (
            <div className="relative flex flex-col gap-[18px] px-[34px] py-8">
              <div className="flex flex-col gap-4">
                <p className="font-serif text-[24px] font-normal italic leading-[32px] text-[#091f24]">
                  {getReferralRewardTitle(
                    availableReferralReward,
                    activeSignupReward
                  )}
                </p>
                <p
                  className="font-sans text-sm font-normal leading-[18px] text-[#6b7070]"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {getReferralRewardDescription(
                    availableReferralReward,
                    activeSignupReward?.title ?? ""
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative flex flex-col gap-[18px] px-[34px] py-16">
              <p className="text-center font-serif text-[24px] font-normal italic leading-[32px] text-[#091f24]">
                Send {activeSignupReward?.title} to your friends and family.
              </p>
            </div>
          )}
        </div>
        {/* Buttons - 16px gap between them */}
        <div className="flex w-full shrink-0 flex-col items-center gap-4">
          <Button fullWidth onClick={handleReferFriendClick}>
            {shareButtonLabel}
          </Button>
          {renderCopyLinkButton()}
          <TextButton className="px-3 font-normal" onClick={handleNext}>
            I&apos;ll do it later
          </TextButton>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
}
